import {
  useCallback, useEffect, useMemo, useRef, useState,
} from 'react';
import { ColDef, RowClickedEvent } from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';
import MainFrame from '../../components/MainFrame/mainFrame';
import styles from './shippingAddressListPage.module.css';
import stylesMain from '../main.module.css';
import usePageTitle from '../../hooks/title.hook';
import {
  useCollectAndDeliverDeleteApi,
  useCollectAndDeliverRegisterApi,
  useCollectAndDeliverSearchApi,
  useCollectAndDeliverUpdateApi,
} from '../../hooks/api/collectAndDeliver.hook';
import { CollectAndDeliver, CollectAndDeliverUpdateForm } from '../../services/http/collectAndDeliver.api';
import Button from '../../components/Button/button';
import AlertModal, { alertModalInitialState } from '../../components/AlertModal';
import BaseTable from '../../components/BaseTable';
import ButtonTableDownload from '../../components/BaseTable/ButtonTableDownload';
import Breadcrumb from '../../components/Breadcrumb';
import routes from '../../utils/routes';
import Formatter from '../../utils/formatters';
import { ApiError } from '../../services/http';
import { useMessageModal } from '../../hooks/modal.hook';
import AGUtils from '../../utils/ag-grid.utils';
import AutoCompleteInput from '../../components/AutoCompleteInput';
import { DropDownMenuItem } from '../../components/DropdownMenu';
import { LogControlName, LogFormName } from '../../utils/log.utils';
import { useCreateLogApi } from '../../hooks/api/log.hook';
import useEffectOnce from '../../hooks/useEffectOnce.hook';
import LoadingOverlay from '../../components/LoadingOverlay';
import RegistrationUpdateInformation from '../../components/RegistrationUpdateInformation';

/**
 * テーブル セル’ボタン’表示のプロップスインターフェース
 */
export interface TableBtnCellRenderProps {
  /** 取り扱うデータ */
  data: CollectAndDeliver,
  /** ボタンクリック時のハンドラー */
  onClick: (user: CollectAndDeliver) => void
}

/**
 * テーブル セル’ボタン’表示のコンポーネント
 */
function TableBtnCellRender({ data, onClick }: TableBtnCellRenderProps) {
  return (
    <div>
      <button
        type="button"
        className={`${styles.tableButton} ${stylesMain['btn-gray']}`}
        style={{ cursor: 'pointer' }}
        onClick={() => onClick(data)}
      >
        変更
      </button>
    </div>
  );
}

/**
 * 集配先一覧ページ
 */
function Component() {
  usePageTitle('集配先一覧');
  const openMessageModal = useMessageModal();

  // Refs
  const gridRef = useRef<AgGridReact<CollectAndDeliver>>(null);

  // States
  const [collectAndDelivers, setCollectAndDelivers] = useState<CollectAndDeliver[]>([]);
  const [tableData, setTableData] = useState<CollectAndDeliver[]>([]);
  const [form, setForm] = useState<CollectAndDeliverUpdateForm | null>(null);
  const [modal, setModal] = useState(alertModalInitialState);
  const [isEditMode, setIsEditMode] = useState(false);

  const [selectedCollectAndDeliver, setSelectedCollectAndDeliver] = useState<CollectAndDeliver | undefined>(undefined);
  const [searchedCollectAndDeliver, setSearchedCollectAndDeliver] = useState<CollectAndDeliver | undefined>(undefined);

  // API's
  const { request: collectAndDeliverSearchApiRequest } = useCollectAndDeliverSearchApi();
  const collectAndDeliverRegisterApi = useCollectAndDeliverRegisterApi();
  const collectAndDeliverUpdateApi = useCollectAndDeliverUpdateApi();
  const collectAndDeliverDeleteApi = useCollectAndDeliverDeleteApi();
  const { request: createLog } = useCreateLogApi();

  // Methods
  const resetPage = useCallback(async () => {
    setSelectedCollectAndDeliver(undefined);
    setForm(null);
    setIsEditMode(false);
    const res = await collectAndDeliverSearchApiRequest();
    setCollectAndDelivers(res);
  }, [collectAndDeliverSearchApiRequest]);

  // Callbacks
  const onClickSave = useCallback(async () => {
    if (!form) return;
    const isRegister = !selectedCollectAndDeliver?.ownerCd;

    setModal({
      open: true,
      text: isRegister ? '新規登録を行います。よろしいですか？' : '集配先情報を更新します。よろしいですか？',
      async onConfirm() {
        setModal({ ...modal, open: false });
        try {
          if (isRegister) {
            await collectAndDeliverRegisterApi.request({
              deliveryAddress1: form.deliveryAddress1,
              deliveryAddress2: form.deliveryAddress2,
              deliveryAddress3: form.deliveryAddress3,
              deliveryName: form.deliveryName,
              deliveryCd: form.deliveryCd,
              deliveryPhone: form.deliveryPhone,
              deliveryPostNo: form.deliveryPostNo,
              deliveryFax: form.deliveryFax,
              deliveryName4: form.deliveryName4,
              deliverySName: form.deliverySName,
              deliveryName3: form.deliveryName3,
            });
          } else {
            await collectAndDeliverUpdateApi.request(form);
          }
          setIsEditMode(false);
          resetPage();
        } catch (e) {
          openMessageModal((e as ApiError)?.message);
        }
      },
      onCancel: () => setModal({ ...modal, open: false }),
    });
  }, [form, selectedCollectAndDeliver]);

  const onClickDelete = useCallback(async () => {
    if (!form) return;
    if (!selectedCollectAndDeliver) return;
    const deleteForm = {
      deliveryCd: form.deliveryCd,
      updateDatetime: form.updateDatetime,
    };
    setModal({
      open: true,
      text: '集配先情報を削除します。よろしいですか？',
      async onConfirm() {
        setModal({ ...modal, open: false });
        try {
          await collectAndDeliverDeleteApi.request(deleteForm);
        } catch (e) {
          openMessageModal((e as ApiError)?.message);
        }
        setCollectAndDelivers(collectAndDelivers.filter((item) => item.ownerCd !== selectedCollectAndDeliver.ownerCd && item.deliveryCd !== selectedCollectAndDeliver.deliveryCd));
        resetPage();
      },
      onCancel: () => setModal({ ...modal, open: false }),
    });
  }, [form]);

  const onClickNewRegistration = useCallback(() => {
    const createNewForm = () => {
      setForm({
        actFlg: '1',
        deliveryAddress1: '',
        deliveryAddress2: '',
        deliveryAddress3: '',
        deliveryCd: '',
        deliveryFax: '',
        deliveryName: '',
        deliveryName3: '',
        deliveryName4: '',
        deliveryPhone: '',
        deliveryPostNo: '',
        updateUser: '',
      });
      setSelectedCollectAndDeliver(undefined);
      setIsEditMode(true);
    };

    if (isEditMode && selectedCollectAndDeliver?.ownerCd) {
      setModal({
        open: true,
        text: '入力内容は廃棄されます。遷移してよろしいですか？',
        onConfirm: () => {
          setModal({ ...modal, open: false });
          createNewForm();
        },
        onCancel: () => setModal({ ...modal, open: false }),
      });
    } else {
      createNewForm();
    }
  }, [isEditMode, selectedCollectAndDeliver, modal]);

  const onClickCancel = useCallback(() => {
    if (isEditMode) {
      setModal({
        open: true,
        text: '入力内容は破棄されます。よろしいですか？',
        onConfirm: () => {
          setModal({ ...modal, open: false });
          setIsEditMode(false);
          if (selectedCollectAndDeliver) {
            setForm({
              deliveryCd: selectedCollectAndDeliver.deliveryCd,
              deliveryName: selectedCollectAndDeliver.deliveryName,
              deliveryPostNo: selectedCollectAndDeliver.deliveryPostNo,
              deliveryAddress1: selectedCollectAndDeliver.deliveryAddress1,
              deliveryAddress2: selectedCollectAndDeliver.deliveryAddress2,
              deliveryPhone: selectedCollectAndDeliver.deliveryPhone,
              deliveryName3: selectedCollectAndDeliver.deliveryName3,
              deliveryAddress3: selectedCollectAndDeliver.deliveryAddress3,
              actFlg: selectedCollectAndDeliver.actFlg,
              deliveryFax: selectedCollectAndDeliver.deliveryFax,
              deliveryName4: selectedCollectAndDeliver.deliveryName4,
              updateDatetime: selectedCollectAndDeliver.updateDatetime,
              updateUser: selectedCollectAndDeliver.updateUser,
            });
          }
        },
        onCancel: () => setModal({ ...modal, open: false }),
      });
    } else {
      setModal({
        open: true,
        text: '入力内容は破棄されます。遷移してよろしいですか？',
        onConfirm: () => {
          setModal({ ...modal, open: false });
          setForm(null);
          setSelectedCollectAndDeliver(undefined);
          setIsEditMode(false);
        },
        onCancel: () => setModal({ ...modal, open: false }),
      });
    }
  }, [modal, isEditMode, selectedCollectAndDeliver]);

  const onRowClicked = useCallback((event: RowClickedEvent<CollectAndDeliver>) => {
    const target = event.event?.target as HTMLElement;
    const isButtonClick = target?.closest('button') || target?.closest('[field="button"]');

    if (event.data && !isButtonClick) {
      const isSameRow = selectedCollectAndDeliver?.deliveryCd === event.data.deliveryCd;

      if (isEditMode && !isSameRow && selectedCollectAndDeliver?.ownerCd) {
        setModal({
          open: true,
          text: '入力内容は廃棄されます。遷移してよろしいですか？',
          onConfirm: () => {
            setModal({ ...modal, open: false });
            setSelectedCollectAndDeliver(event.data);
            setIsEditMode(false);
          },
          onCancel: () => setModal({ ...modal, open: false }),
        });
      } else {
        setSelectedCollectAndDeliver(event.data);
        if (!isSameRow) {
          setIsEditMode(false);
        }
      }
    }
  }, [isEditMode, selectedCollectAndDeliver, modal]);

  const isFormValid = useMemo(() => {
    if (!form) return false;
    const {
      deliveryCd, deliveryName, deliveryPostNo, deliveryAddress1, deliveryAddress2, deliveryName3, deliveryPhone,
    } = form;
    return deliveryName && deliveryPostNo && deliveryAddress1 && deliveryAddress2 && deliveryName3 && deliveryPhone && deliveryCd;
  }, [form]);

  const colDef = useMemo((): ColDef<CollectAndDeliver>[] => [
    AGUtils.colAutoIncrement('no'),
    {
      headerName: '',
      resizable: true,
      suppressColumnsToolPanel: true,
      field: 'button',
      cellRenderer: TableBtnCellRender,
      cellRendererParams: {
        onClick(item: CollectAndDeliver) {
          if (isEditMode && selectedCollectAndDeliver && selectedCollectAndDeliver.deliveryCd !== item.deliveryCd && selectedCollectAndDeliver.ownerCd) {
            setModal({
              open: true,
              text: '入力内容は廃棄されます。遷移してよろしいですか？',
              onConfirm: () => {
                setModal({ ...modal, open: false });
                setSelectedCollectAndDeliver(item);
                setIsEditMode(true);
              },
              onCancel: () => setModal({ ...modal, open: false }),
            });
          } else {
            setSelectedCollectAndDeliver(item);
            setIsEditMode(true);
          }
        },
      },
      width: 60,
    },
    AGUtils.colDefault('deliveryCd', '集配先コード'),
    {
      headerName: '集配先名称',
      resizable: true,
      field: 'deliveryName',
      cellClass: 'textFormat',
      flex: 1,
    }, {
      headerName: '住所',
      resizable: true,
      field: 'address',
      valueGetter: ({ data }) => [data?.deliveryPostNo, data?.deliveryAddress1, data?.deliveryAddress2, data?.deliveryAddress3].join(' '),
      flex: 1,
      cellClass: 'textFormat',
    },
  ], [isEditMode, modal, selectedCollectAndDeliver]);

  useEffect(() => {
    resetPage();
  }, []);

  useEffect(() => {
    setTableData(collectAndDelivers);
  }, [collectAndDelivers]);

  const onCollectAndDeliverSelected = useCallback(async (selected: CollectAndDeliver) => {
    setSearchedCollectAndDeliver(selected);
  }, []);

  const collectAndDeliverListOptions = useMemo(() => {
    const result: DropDownMenuItem<CollectAndDeliver>[] = [];

    collectAndDelivers.forEach((collect) => {
      result.push({
        text: `${collect.deliveryCd}：${collect.deliveryName}`,
        value: collect,
      });
    });

    return result;
  }, [collectAndDelivers]);

  // Sets table data every time a searchedCollectAndDeliver is selected
  useEffect(() => {
    if (!searchedCollectAndDeliver) return setTableData(collectAndDelivers);
    createLog(LogFormName.ShippingAddressList, LogControlName.Search);

    return setTableData(collectAndDelivers.filter((item) => item.deliveryCd === searchedCollectAndDeliver.deliveryCd || item.deliveryName === searchedCollectAndDeliver.deliveryName));
  }, [searchedCollectAndDeliver, collectAndDelivers]);

  useEffect(() => {
    if (selectedCollectAndDeliver) {
      setForm({
        deliveryCd: selectedCollectAndDeliver.deliveryCd,
        deliveryName: selectedCollectAndDeliver.deliveryName,
        deliveryPostNo: selectedCollectAndDeliver.deliveryPostNo,
        deliveryAddress1: selectedCollectAndDeliver.deliveryAddress1,
        deliveryAddress2: selectedCollectAndDeliver.deliveryAddress2,
        deliveryPhone: selectedCollectAndDeliver.deliveryPhone,
        deliveryName3: selectedCollectAndDeliver.deliveryName3,
        deliveryAddress3: selectedCollectAndDeliver.deliveryAddress3,
        actFlg: selectedCollectAndDeliver.actFlg,
        deliveryFax: selectedCollectAndDeliver.deliveryFax,
        deliveryName4: selectedCollectAndDeliver.deliveryName4,
        updateDatetime: selectedCollectAndDeliver.updateDatetime,
        updateUser: selectedCollectAndDeliver.updateUser,
      });
    }
  }, [selectedCollectAndDeliver]);

  useEffectOnce(() => {
    createLog(LogFormName.ShippingAddressList, LogControlName.Show);
  });

  return (
    <MainFrame borderBox>
      <div className={`${styles.mainContent} ${stylesMain['pt-74px']}`}>
        <Breadcrumb crumbs={[
          { label: 'マスタ管理', route: routes.masterDataSettingGeneral },
          { label: document.title }]}
        />
        <div className={`${styles.mainContentGrid} ${stylesMain['h-100']}`}>
          <div className={`${styles.borderRightGrey} ${stylesMain['h-100']}`}>
            <div className={`${styles.tableContainer}`}>
              <div>
                <div style={{ display: 'flex', justifyItems: 'end' }}>
                  <AutoCompleteInput
                    className={`${styles.searchSelect} ${stylesMain['w-25']}`}
                    value={searchedCollectAndDeliver ? `${searchedCollectAndDeliver.deliveryCd}：${searchedCollectAndDeliver.deliveryName}` : ''}
                    placeholder="検索"
                    options={collectAndDeliverListOptions}
                    onSelect={onCollectAndDeliverSelected}
                    deselectOption
                    searchIcon
                    onDeselect={() => {
                      setSearchedCollectAndDeliver(undefined);
                    }}
                  />
                </div>
                <div className={stylesMain['mt-10']}>
                  <Button
                    color="lighterGray"
                    size="smaller"
                    onClick={onClickNewRegistration}
                  >
                    集配先を登録
                  </Button>
                </div>
              </div>
              <div className={` ${styles.borderTopGrey}`} style={{ flex: 1 }}>
                <div className={`${stylesMain['text-right']}`}>
                  <div style={{ display: 'flex', justifyContent: 'end', padding: '10px' }}>
                    <ButtonTableDownload
                      tableRef={gridRef}
                      columnsToExport={['no', 'deliveryCd', 'deliveryName', 'address']}
                      logFormName={LogFormName.ShippingAddressList}
                    />
                  </div>
                </div>
                <div className={`${styles['table-height']}`}>
                  <BaseTable<CollectAndDeliver>
                    tableRef={gridRef}
                    formName="shippingAddressTable"
                    className={styles.table}
                    columnDefs={colDef}
                    rowData={tableData}
                    createLogOnDownloadTableData={LogFormName.ShippingAddressList}
                    onRowClicked={onRowClicked}
                    sideBar
                  />
                </div>
              </div>
            </div>
          </div>
          {form
            && (
              <div className={`${stylesMain['h-100']} ${styles.formContainer}`}>
                <div className={styles.formHeader}>
                  集配先詳細
                </div>
                <div className={styles.formContent}>
                  <div className={styles.formContentInner}>
                    <div className={styles.formWrapper}>
                      <div className={`${styles.form} ${stylesMain['w-100']}`}>
                        <div className={styles.input_field}>
                          {selectedCollectAndDeliver?.ownerCd && form.deliveryCd ? (
                            <>
                              <label>集配先コード</label>
                              <span className={styles.displayText}>{selectedCollectAndDeliver.deliveryCd}</span>
                            </>
                          ) : (
                            <>
                              <label>
                                集配先コード
                                {isEditMode && (
                                  <span
                                    className={`${stylesMain['text-small']} ${stylesMain['text-danger']} ${stylesMain['float-right']} ${stylesMain['mr-3']}`}
                                  >
                                    ※
                                  </span>
                                )}
                              </label>
                              <input
                                maxLength={30}
                                value={form.deliveryCd}
                                onChange={(e) => setForm({
                                  ...form,
                                  deliveryCd: e.target.value,
                                })}
                              />
                            </>
                          )}
                        </div>
                        <div className={styles.input_field}>
                          <label>
                            集配先名称
                            {isEditMode && <span className={`${stylesMain['text-small']} ${stylesMain['text-danger']} ${stylesMain['float-right']} ${stylesMain['mr-3']}`}>※</span>}
                          </label>
                          {isEditMode ? (
                            <input
                              maxLength={100}
                              value={form.deliveryName}
                              onChange={(e) => setForm({ ...form, deliveryName: e.target.value })}
                            />
                          ) : (
                            <span className={styles.displayText}>{form.deliveryName}</span>
                          )}
                        </div>
                        <div className={styles.input_field}>
                          <label>
                            郵便番号
                            {isEditMode && <span className={`${stylesMain['text-small']} ${stylesMain['text-danger']} ${stylesMain['float-right']} ${stylesMain['mr-3']}`}>※</span>}
                          </label>
                          {isEditMode ? (
                            <input
                              maxLength={10}
                              value={form.deliveryPostNo}
                              onChange={(e) => setForm({ ...form, deliveryPostNo: e.target.value })}
                            />
                          ) : (
                            <span className={styles.displayText}>{form.deliveryPostNo}</span>
                          )}
                        </div>
                        <div className={styles.input_field}>
                          <label>
                            都道府県
                            {isEditMode && <span className={`${stylesMain['text-small']} ${stylesMain['text-danger']} ${stylesMain['float-right']} ${stylesMain['mr-3']}`}>※</span>}
                          </label>
                          {isEditMode ? (
                            <input
                              maxLength={100}
                              value={form.deliveryAddress1}
                              onChange={(e) => setForm({ ...form, deliveryAddress1: e.target.value })}
                            />
                          ) : (
                            <span className={styles.displayText}>{form.deliveryAddress1}</span>
                          )}
                        </div>
                        <div className={styles.input_field}>
                          <label>
                            市区町村番地
                            {isEditMode && <span className={`${stylesMain['text-small']} ${stylesMain['text-danger']} ${stylesMain['float-right']} ${stylesMain['mr-3']}`}>※</span>}
                          </label>
                          {isEditMode ? (
                            <input
                              maxLength={100}
                              value={form.deliveryAddress2}
                              onChange={(e) => setForm({ ...form, deliveryAddress2: e.target.value })}
                            />
                          ) : (
                            <span className={styles.displayText}>{form.deliveryAddress2}</span>
                          )}
                        </div>
                        <div className={styles.input_field}>
                          <label>建物名</label>
                          {isEditMode ? (
                            <input
                              maxLength={100}
                              value={form.deliveryAddress3}
                              onChange={(e) => setForm({ ...form, deliveryAddress3: e.target.value })}
                            />
                          ) : (
                            <span className={styles.displayText}>{form.deliveryAddress3 || ''}</span>
                          )}
                        </div>
                        <div className={styles.input_field}>
                          <label>
                            担当者名
                            {isEditMode && <span className={`${stylesMain['text-small']} ${stylesMain['text-danger']} ${stylesMain['float-right']} ${stylesMain['mr-3']}`}>※</span>}
                          </label>
                          {isEditMode ? (
                            <input
                              maxLength={100}
                              value={form.deliveryName3}
                              onChange={(e) => setForm({ ...form, deliveryName3: e.target.value })}
                            />
                          ) : (
                            <span className={styles.displayText}>{form.deliveryName3}</span>
                          )}
                        </div>
                        <div className={styles.input_field}>
                          <label>
                            電話番号
                            {isEditMode && <span className={`${stylesMain['text-small']} ${stylesMain['text-danger']} ${stylesMain['float-right']} ${stylesMain['mr-3']}`}>※</span>}
                          </label>
                          {isEditMode ? (
                            <input
                              maxLength={25}
                              value={form.deliveryPhone}
                              onChange={(e) => setForm({ ...form, deliveryPhone: e.target.value })}
                            />
                          ) : (
                            <span className={styles.displayText}>{form.deliveryPhone}</span>
                          )}
                        </div>
                        <div className={styles.input_field}>
                          <label>FAX番号</label>
                          {isEditMode ? (
                            <input
                              maxLength={25}
                              value={form.deliveryFax}
                              onChange={(e) => setForm({ ...form, deliveryFax: e.target.value })}
                            />
                          ) : (
                            <span className={styles.displayText}>{form.deliveryFax || ''}</span>
                          )}
                        </div>
                        {form.deliveryCd && selectedCollectAndDeliver?.ownerCd
                        && (
                          <div className={styles.input_field}>
                            <label>この集配先を無効にする</label>
                            <input
                              type="checkbox"
                              className={styles.inputCheckbox}
                              checked={form.actFlg === '0'}
                              onChange={(e) => setForm({ ...form, actFlg: e.target.checked ? '0' : '1' })}
                              disabled={!isEditMode}
                            />
                          </div>
                        )}
                        <div className={`${styles.input_field} ${styles.textareaField}`}>
                          <label>集配先備考</label>
                          {isEditMode ? (
                            <textarea
                              maxLength={100}
                              className={styles.textArea}
                              value={form.deliveryName4}
                              onChange={(e) => setForm({ ...form, deliveryName4: e.target.value })}
                            />
                          ) : (
                            <span className={`${styles.displayText} ${styles.textAreaDisplay}`}>{form.deliveryName4 || ''}</span>
                          )}
                        </div>
                      </div>
                    </div>
                    {form.deliveryCd && selectedCollectAndDeliver?.ownerCd
                    && (
                      <div className={styles.registrationUpdateRow}>
                        <div className={`${stylesMain['mt-4']} ${styles.title}`}>
                          登録更新情報
                        </div>
                        <div>
                          <RegistrationUpdateInformation
                            createdBy={selectedCollectAndDeliver.registerUser}
                            createdAt={selectedCollectAndDeliver.createDatetime ? Formatter.toDisplayDate(selectedCollectAndDeliver.createDatetime, Formatter.defaultDateTimeFormat) : ''}
                            updatedBy={selectedCollectAndDeliver.updateUser}
                            updatedAt={selectedCollectAndDeliver.updateDatetime ? Formatter.toDisplayDate(selectedCollectAndDeliver.updateDatetime, Formatter.defaultDateTimeFormat) : ''}
                          />
                        </div>
                      </div>
                    )}
                  </div>
                </div>
                {isEditMode && (
                  <div className={`${styles.footerFixed}`}>
                    <div className={styles.footerButtons}>
                      <div>
                        {selectedCollectAndDeliver && selectedCollectAndDeliver.ownerCd && <Button size="smaller" color="error" onClick={onClickDelete}>削除</Button>}
                      </div>
                      <div>
                        <Button size="smaller" color="lighterGray" onClick={onClickCancel}>キャンセル</Button>
                        <Button className={`${stylesMain['ml-10']}`} disabled={!isFormValid} size="smaller" color="primary" onClick={onClickSave}>
                          保存
                        </Button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
        </div>
        <AlertModal open={modal.open} text={modal.text} onConfirm={modal.onConfirm} onCancel={modal.onCancel} textCenter />
        {(collectAndDeliverRegisterApi.loading || collectAndDeliverUpdateApi.loading || collectAndDeliverDeleteApi.loading) && <LoadingOverlay />}
      </div>
    </MainFrame>
  );
}

export default Component;
